import {
  evaluateCyanOwnerAuthority,
  type CyanOwnerAuthorityRisk,
} from "./cyanOwnerAuthority";

export interface CyanOperationalApprovalInput {
  action: string;
  risk: CyanOwnerAuthorityRisk;
  requiresApproval: boolean;
  reversible: boolean;
  governedScope: boolean;
  authorizationExists?: boolean;

  destroysSs?: boolean;
  destroysExternalProject?: boolean;
  destroysCriticalProductionData?: boolean;
  revokesOwnerAccess?: boolean;
  exposesSecrets?: boolean;
  crossesUnknownAuthorityBoundary?: boolean;
}

export interface CyanOperationalApprovalResult {
  allowed: boolean;
  requiresHumanOwner: boolean;
  approvalSatisfiedBy:
    "NOT_REQUIRED" | "CYAN_OPERATIONAL_OWNER" | "HUMAN_BREAK_GLASS" | "DENIED";
  reason: string;
}

/**
 * Compatibility authority for legacy `requiresApproval` paths.
 *
 * `requiresApproval` no longer means "stop and ask the human".
 * It means "obtain an owner-authority decision".
 *
 * Cyan is the delegated operational owner inside governed boundaries.
 */
export function evaluateCyanOperationalApproval(
  input: CyanOperationalApprovalInput,
): CyanOperationalApprovalResult {
  if (!input.requiresApproval) {
    return {
      allowed: true,
      requiresHumanOwner: false,
      approvalSatisfiedBy: "NOT_REQUIRED",
      reason: "Operation does not require elevated approval.",
    };
  }

  const authority = evaluateCyanOwnerAuthority({
    action: input.action,
    risk: input.risk,
    reversible: input.reversible,
    governedScope: input.governedScope,
    authorizationExists: input.authorizationExists,
    destroysSs: input.destroysSs,
    destroysExternalProject: input.destroysExternalProject,
    destroysCriticalProductionData: input.destroysCriticalProductionData,
    revokesOwnerAccess: input.revokesOwnerAccess,
    exposesSecrets: input.exposesSecrets,
    crossesUnknownAuthorityBoundary: input.crossesUnknownAuthorityBoundary,
  });

  if (authority.authorized) {
    return {
      allowed: true,
      requiresHumanOwner: false,
      approvalSatisfiedBy: "CYAN_OPERATIONAL_OWNER",
      reason: authority.reason,
    };
  }

  if (authority.requiresHumanOwner) {
    return {
      allowed: false,
      requiresHumanOwner: true,
      approvalSatisfiedBy: "HUMAN_BREAK_GLASS",
      reason: authority.reason,
    };
  }

  return {
    allowed: false,
    requiresHumanOwner: false,
    approvalSatisfiedBy: "DENIED",
    reason: authority.reason,
  };
}

export type CyanGoalLikeRisk = "low" | "medium" | "high" | "critical";

/**
 * Maps planning-domain risk into execution owner-authority risk.
 *
 * This does not weaken BLACK protection. BLACK is reserved for
 * destructive execution classification at the execution boundary.
 */
export function goalRiskToCyanOwnerRisk(
  risk: CyanGoalLikeRisk,
): CyanOwnerAuthorityRisk {
  switch (risk) {
    case "low":
      return "GREEN";

    case "medium":
      return "YELLOW";

    case "high":
    case "critical":
      return "RED";
  }
}
